import { useId } from "react";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";

/**
 * One linear wash, drawn as an SVG rect.
 *
 * React Native has no gradient of its own, and `expo-linear-gradient` is a native module: adding
 * one would cost this app its over-the-air path for a shape `react-native-svg` — already a
 * dependency — draws directly (ADR-0144).
 *
 * Shared by `SlabFrame` and `SlabQuote`, the only two components allowed a gradient at all. It is
 * deliberately not a general-purpose primitive: a surface that wants one is a bug, not a caller.
 *
 * **It is positioned, and on web that decides what it covers.** A bare `<svg>` is `position:
 * static`, so an absolutely positioned wash paints over any sibling mark drawn in flow whatever
 * the DOM order — that is how the plate's bevel came to cover PSA's logo. Content that must sit
 * above a wash goes in a `View`, which react-native-web positions.
 */
export function Wash(
  { width, height, stops, x2 = "0", y2 = "1" }: {
    width: number;
    height: number;
    /** Gradient stops from start to end; `offset` runs "0" to "1". */
    stops: readonly { offset: string; color: string; opacity: number; }[];
    /** The gradient's end x as a fraction of the width; "0" is straight down. */
    x2?: string;
    /** The gradient's end y as a fraction of the height; "1" runs the full height. */
    y2?: string;
  },
) {
  // Gradient ids share one document on web, where every slab on a grid page renders into the same
  // DOM, so each instance mints its own. `useId` punctuates with colons, which `url(#…)` will not
  // resolve.
  const id = `wash-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <Svg
      width={width}
      height={height}
      pointerEvents="none"
      style={{ position: "absolute", top: 0, left: 0 }}
    >
      <Defs>
        <LinearGradient id={id} x1="0" y1="0" x2={x2} y2={y2}>
          {stops.map((stop) => (
            <Stop
              key={stop.offset}
              offset={stop.offset}
              stopColor={stop.color}
              stopOpacity={stop.opacity}
            />
          ))}
        </LinearGradient>
      </Defs>
      <Rect x={0} y={0} width={width} height={height} fill={`url(#${id})`} />
    </Svg>
  );
}
